/**
 * Imperative <head>-Verwaltung für die SEO-Einstiegsseiten.
 *
 * Die App ist eine CRA-SPA ohne Server-Rendering. Damit Crawler und
 * Social-Vorschauen korrekte Angaben sehen, geschieht ZWEIERLEI:
 *   1. Build-Zeit: `scripts/prerender-seo.js` schreibt je Einstiegsseite
 *      eine echte HTML-Datei mit Titel/Meta/H1 (statisch lesbar).
 *   2. Laufzeit: dieser Hook setzt dieselben Angaben, sobald React
 *      rendert — unabhängig davon, ob die Datei ausgeliefert wurde.
 *
 * WICHTIG: `index.html` liefert bereits `robots`, `og:*` und `twitter:*`
 * der Startseite. Diese Knoten werden ÜBERSCHRIEBEN (nicht ergänzt) und
 * beim Verlassen der Seite auf den Ausgangswert zurückgesetzt — zwei
 * widersprüchliche `robots`- oder `og:url`-Angaben auf einer Seite sind
 * ein echter SEO-Fehler, und Scraper lesen meist den ersten Treffer.
 */
import { useEffect, useRef } from "react";

const MANAGED = "data-seo-managed";

/**
 * `noindex,follow` für Seiten, die ein statischer Host zwangsläufig mit
 * HTTP 200 ausliefert (SPA-Fallback). Ohne das landen „Soft 404" im
 * Index. Der Ausgangswert wird beim Verlassen wiederhergestellt.
 */
export function useNoindex() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      // Fehlt der Knoten, wird er angelegt — sonst bliebe eine
      // Soft-404-Seite indexierbar, nur weil `index.html` gerade keine
      // robots-Angabe trug.
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute(MANAGED, "1");
      document.head.appendChild(el);
      el.setAttribute("content", "noindex,follow");
      return () => el.remove();
    }
    const prev = el.getAttribute("content");
    el.setAttribute("content", "noindex,follow");
    return () => el.setAttribute("content", prev ?? "index, follow");
  }, []);
}

export default function useSeoHead({
  lang, title, description, canonical, alternates = [], xDefault,
  robots = "index,follow", jsonLd = [], ogImage, ogLocale, enabled = true,
}) {
  const altKey = alternates.map((a) => `${a.lang}:${a.href}`).join("|");
  const jsonLdKey = JSON.stringify(jsonLd);
  const restoreRef = useRef([]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    // `enabled: false` heisst: diese Seite verwaltet den Kopf NICHT.
    // Gebraucht wird das, wenn die Route auf die 404-Ansicht fällt —
    // Effekte laufen von innen nach aussen, ohne diesen Ausstieg würde
    // das hier gesetzte `index` das `noindex` der 404-Seite
    // überschreiben.
    if (!enabled) return undefined;
    const head = document.head;
    const restore = [];

    const remember = (el, attr) =>
      restore.push({ el, attr, value: el.getAttribute(attr) });

    /** Vorhandenen Knoten überschreiben, sonst einen eigenen anlegen. */
    const upsertMeta = (attr, key, content) => {
      const found = head.querySelector(`meta[${attr}="${key}"]`);
      if (found) {
        remember(found, "content");
        found.setAttribute("content", content);
        return;
      }
      const el = document.createElement("meta");
      el.setAttribute(attr, key);
      el.setAttribute("content", content);
      el.setAttribute(MANAGED, "1");
      head.appendChild(el);
    };

    const addLink = (rel, href, extra = {}) => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      el.setAttribute("href", href);
      Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute(MANAGED, "1");
      head.appendChild(el);
    };

    head.querySelectorAll(`[${MANAGED}]`).forEach((n) => n.remove());

    const prevTitle = document.title;
    const prevLang = document.documentElement.lang;
    document.title = title;
    document.documentElement.lang = lang;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    if (ogLocale) upsertMeta("property", "og:locale", ogLocale);
    if (ogImage) upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    const canonicalEl = head.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      remember(canonicalEl, "href");
      canonicalEl.setAttribute("href", canonical);
    } else {
      addLink("canonical", canonical);
    }

    alternates.forEach((a) => addLink("alternate", a.href, { hreflang: a.lang }));
    if (xDefault) addLink("alternate", xDefault, { hreflang: "x-default" });

    jsonLd.filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(obj);
      s.setAttribute(MANAGED, "1");
      head.appendChild(s);
    });

    restoreRef.current = restore;
    return () => {
      head.querySelectorAll(`[${MANAGED}]`).forEach((n) => n.remove());
      restoreRef.current.forEach(({ el, attr, value }) => {
        if (value === null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      });
      restoreRef.current = [];
      document.title = prevTitle;
      document.documentElement.lang = prevLang;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, title, description, canonical, robots, altKey, xDefault, ogImage,
      ogLocale, jsonLdKey, enabled]);
}
